import moment from "moment";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import withStyles from "react-jss";
import Select, { components } from "react-select";
import COLOURS from "util/colours";
import { H2, H3 } from "visual-components/util/texts";
import Pagination from "./pagination";

const styles = {
  container: {
    padding: 40,
  },
  heading: {
    marginBottom: 24,
  },
  filterSection: {
    marginBottom: 24,
    padding: 16,
    border: `1px solid ${COLOURS.silver}`,
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 24,
  },
  filterItem: {
    flex: "1 1 250px",
    minWidth: 220,
    display: "flex",
    flexDirection: "column",
    marginLeft: 12,
  },
  filterLabel: {
    marginBottom: 6,
    fontWeight: 500,
    fontSize: 14,
    color: "#333",
  },
  filterInput: {
    width: "100%",
    padding: "8px 12px",
    fontSize: "14px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    transition: "border-color 0.2s",

    "&:focus": {
      borderColor: "#007bff",
      boxShadow: "0 0 3px rgba(0,123,255,0.5)",
    },
  },
  filterButton: {
    marginLeft: 16,
    backgroundColor: "transparent",
    border: "1px solid #007FFF",
    color: "#007FFF",
    padding: "6px 12px",
    borderRadius: 6,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#007FFF",
      color: "#fff",
    },
  },
  loader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 16,
    "& .spinner": {
      width: 24,
      height: 24,
      border: "4px solid #007FFF",
      borderTop: "4px solid transparent",
      borderRadius: "50%",
      animation: "$spin 1s linear infinite",
    },
  },
  tableWrapper: {
    marginTop: 16,
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    fontSize: 14,
  },
  tableHeader: {
    backgroundColor: COLOURS.silver,
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  th: {
    padding: "12px 16px",
    textAlign: "left",
    borderBottom: "2px solid #ddd",
    fontWeight: 600,
    backgroundColor: "#f5f7fa",
  },
  td: {
    padding: "12px 16px",
    borderBottom: "1px solid #eee",
  },
  tableRow: {
    "&:nth-child(even)": {
      backgroundColor: "#fafafa",
    },
    "&:hover": {
      backgroundColor: "#eaf4ff",
      transition: "0.2s",
    },
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
};

function CSAdminSweepsManager(props) {
  const { classes } = props;
  const [campaigns, setCampaigns] = useState([]);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedMonth, setSelectedMonth] = useState(moment().month());
  const [fromDate, setFromDate] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().endOf("month").format("YYYY-MM-DD"));
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: "status", direction: "desc" });
  const [statusFilter, setStatusFilter] = useState("All");
  const [keyword, setKeyword] = useState("");
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Live", label: "Live" },
    { value: "Upcoming", label: "Upcoming" },
    { value: "Completed", label: "Completed" },
  ];
  // Years array
  const startYear = 2010;
  const currentYear = moment().year();
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, i) => {
    const year = startYear + i;
    return {
      value: year,
      label: year.toString(),
    };
  }).sort((a, b) => b.value - a.value);
  // Months array
  const months = React.useMemo(
    () =>
      moment.months().map((m, idx) => ({
        value: idx,
        label: `${m} ${selectedYear}`,
      })),
    [selectedYear], // 👈 recompute when year changes
  );

  // 🔹 When month changes, update fromDate & toDate
  const handleMonthChange = (option) => {
    setSelectedMonth(option.value);
    setFromDate(
      moment({ year: selectedYear, month: option.value }).startOf("month").format("YYYY-MM-DD"),
    );
    setToDate(
      moment({ year: selectedYear, month: option.value }).endOf("month").format("YYYY-MM-DD"),
    );
    setCampaigns([]);
  };
  const handleYearChange = (option) => {
    setSelectedYear(option.value);

    const start = moment({ year: option.value, month: selectedMonth })
      .startOf("month")
      .format("YYYY-MM-DD");

    const end = moment({ year: option.value, month: selectedMonth })
      .endOf("month")
      .format("YYYY-MM-DD");

    setFromDate(start);
    setToDate(end);
  };

  const formatDate = (date) => {
    if (!date) return null;
    return moment(date).format("YYYY-MM-DD");
  };

  const handleFilter = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", formatDate(fromDate));
      if (toDate) params.append("toDate", formatDate(toDate));
      params.append("page", Math.max(1, parseInt(page, 10))); // ensures page >= 1
      params.append("limit", limit || 50); // fallback limit if undefined
      params.append("statusFilter", statusFilter);
      params.append("keyword", keyword);
      const res = await fetch(`/cs-sweeps?${params.toString()}`);
      const result = await res.json();
      const campaigns = result.campaigns;
      setCampaigns([...campaigns]);
      setTotal(result.total);
      setPage(result.page);
    } catch (err) {
      console.error("Failed to fetch sweeps", err);
    } finally {
      setLoading(false);
    }
  };
  const getStatusColor = (status) => {
    switch (status) {
      case "Live":
        return "#d4edda"; // light green
      case "Completed":
        return "#f8d7da"; // light red
      case "Upcoming":
        return "#fff3cd"; // light yellow
      default:
        return "#ffffff"; // default white
    }
  };
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const sortedCampaigns = React.useMemo(() => {
    if (!campaigns.length) return [];

    const sorted = [...campaigns];
    const { key, direction } = sortConfig;

    sorted.sort((a, b) => {
      let valA = a[key];
      let valB = b[key];

      // Convert dates for comparison
      if (key === "start_date" || key === "end_date") {
        valA = new Date(valA);
        valB = new Date(valB);
      }

      // Ensure case-insensitive sorting for strings
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [campaigns, sortConfig]);

  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <H2 azure>CS Admin - All Sweeps View</H2>
      </div>

      {/* Filter Section */}
      <div className={classes.filterSection}>
        <H3>Filter Sweeps</H3>

        <div className={classes.filterRow}>
          {/* 🔹 Year Dropdown */}
          <div className={classes.filterItem}>
            <label className={classes.filterLabel}>Select Year</label>
            <Select
              value={years.find((y) => y.value === selectedYear)}
              onChange={handleYearChange}
              options={years}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          {/* 🔹 Month Dropdown */}
          <div className={classes.filterItem}>
            <label className={classes.filterLabel}>Select Month</label>
            <Select
              value={months.find((m) => m.value === selectedMonth)}
              onChange={handleMonthChange}
              options={months}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div className={classes.filterItem}>
            <label className={classes.filterLabel}>Filter sweeps</label>
            <Select
              value={statusOptions.find((s) => s.value === statusFilter)}
              onChange={(option) => setStatusFilter(option.value)}
              options={statusOptions}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          <div className={classes.filterItem}>
            <label className={classes.filterLabel}>Filter by sweep name</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search sweep by name..."
              className={classes.filterInput}
            />
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => handleFilter()}
            className={classes.filterButton}
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Processing..." : "Show sweeps"}
          </button>
        </div>
      </div>

      {sortedCampaigns.length > 0 ? (
        <div style={{ marginTop: 32 }}>
          <H3>Results:</H3>
          <div className={classes.filterRow} style={{ marginTop: 15 }}>
            <div className={classes.filterItem} style={{ marginTop: 15 }}>
              Total Campaign(s): {total}
            </div>
          </div>
          <table
            className={classes.table}
            style={{ marginTop: 32, borderSpacing: "8px", borderCollapse: "separate" }}
          >
            <thead className={classes.tableHeader}>
              <tr>
                <th
                  className={classes.th}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("name")}
                >
                  Campaign Name{" "}
                  <span>
                    {sortConfig.key === "name"
                      ? sortConfig.direction === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}{" "}
                    {/* inactive state */}
                  </span>
                </th>
                <th
                  className={classes.th}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("start_date")}
                >
                  Start date{" "}
                  <span>
                    {sortConfig.key === "start_date"
                      ? sortConfig.direction === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}{" "}
                    {/* inactive state */}
                  </span>
                </th>
                <th
                  className={classes.th}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("end_date")}
                >
                  End date{" "}
                  <span>
                    {sortConfig.key === "end_date"
                      ? sortConfig.direction === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}{" "}
                    {/* inactive state */}
                  </span>
                </th>
                <th
                  className={classes.th}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("brand_name")}
                >
                  Host Brand Name{" "}
                  <span>
                    {sortConfig.key === "brand_name"
                      ? sortConfig.direction === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}{" "}
                    {/* inactive state */}
                  </span>
                </th>
                <th
                  className={classes.th}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort("status")}
                >
                  Campaign Status{" "}
                  <span>
                    {sortConfig.key === "status"
                      ? sortConfig.direction === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}{" "}
                    {/* inactive state */}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCampaigns.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: "16px", textAlign: "center", color: "#999" }}>
                    No Campaign found.
                  </td>
                </tr>
              ) : (
                sortedCampaigns.map((campaign) => {
                  let statusColor = "black"; // default

                  if (campaign.status === "Live") {
                    statusColor = "green";
                  } else if (campaign.status === "Completed") {
                    statusColor = "red";
                  } else if (campaign.status === "Upcoming") {
                    statusColor = "orange"; // or yellow
                  }

                  return (
                    <tr
                      key={campaign.id}
                      className={classes.tableRow}
                      style={{
                        backgroundColor: getStatusColor(campaign.status),
                        borderRadius: "8px",
                      }}
                    >
                      <td
                        className={classes.td}
                        style={{ cursor: "pointer", color: "blue" }}
                        onClick={() => window.open(`/app/explore/sweepstakes/${campaign.id}`)}
                      >
                        {campaign.name}
                      </td>
                      <td className={classes.td}>
                        {moment(campaign.start_date).format("YYYY-MM-DD")}
                      </td>
                      <td className={classes.td}>
                        {moment(campaign.end_date).format("YYYY-MM-DD")}
                      </td>
                      <td className={classes.td}>{campaign.brand_name}</td>
                      <td
                        className={classes.td}
                        style={{
                          color: statusColor,
                          fontWeight: "bold",
                          border: `1px solid ${statusColor}`,
                          borderRadius: "8px",
                          padding: "6px 12px",
                          textAlign: "center",
                          backgroundColor: `${statusColor}20`,
                        }}
                      >
                        {campaign.status}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          {sortedCampaigns.length > 0 && (
            <Pagination
              total={total}
              page={page}
              limit={limit}
              onPageChange={(newPage) => handleFilter(newPage)}
            />
          )}
        </div>
      ) : (
        <tr>
          <td colSpan={5} style={{ padding: "16px", textAlign: "center", color: "#999" }}>
            No Sweeps found.
          </td>
        </tr>
      )}
    </div>
  );
}

CSAdminSweepsManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(CSAdminSweepsManager);
